
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";

interface Equipment {
  id: string;
  name: string;
  base_price: number;
}

interface Guard {
  id: string;
  full_name: string;
}

interface EquipmentAssignment {
  equipmentId: string;
  quantity: number;
  isSelected: boolean;
}

interface EquipmentAssignmentProps {
  refreshTrigger: number;
  onDataChange: () => void;
}

// Define schema outside the component to avoid recursive type issues
const assignmentSchema = z.object({
  guardId: z.string().min(1, "Please select a guard"),
  notes: z.string().optional(),
  equipmentAssignments: z.array(
    z.object({
      equipmentId: z.string(),
      quantity: z.number().int().positive("Quantity must be positive"),
      isSelected: z.boolean(),
    })
  ).refine(
    (items) => items.some(item => item.isSelected),
    {
      message: "Select at least one equipment item",
    }
  ),
});

// Define types for form values
type AssignmentFormValues = z.infer<typeof assignmentSchema>;

export const EquipmentAssignment = ({ refreshTrigger, onDataChange }: EquipmentAssignmentProps) => {
  const [guards, setGuards] = useState<Guard[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  
  const form = useForm<AssignmentFormValues>({
    resolver: zodResolver(assignmentSchema),
    defaultValues: {
      guardId: "",
      notes: "",
      equipmentAssignments: [],
    },
  });
  
  useEffect(() => {
    fetchGuards();
    fetchEquipment();
  }, [refreshTrigger]);
  
  const fetchGuards = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name')
        .eq('status', 'active')
        .order('full_name');
        
      if (error) throw error;
      
      setGuards(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load guards: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchEquipment = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('equipment_types')
        .select('id, name, base_price')
        .order('name');
        
      if (error) throw error;

      // Transform the data to match the Equipment interface
      const equipmentItems: Equipment[] = (data || []).map(item => ({
        id: item.id,
        name: item.name,
        base_price: item.base_price || 0,
      }));
      
      // Set up form values for equipment
      form.setValue('equipmentAssignments', equipmentItems.map(item => ({
        equipmentId: item.id,
        quantity: 1,
        isSelected: false,
      })));
      
      setEquipment(equipmentItems);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const onSubmit = async (values: AssignmentFormValues) => {
    try {
      if (!user?.id) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to assign equipment",
          variant: "destructive",
        });
        return;
      }
      
      // Filter only selected equipment
      const selectedEquipment = values.equipmentAssignments.filter(item => item.isSelected);
      
      // Prepare assignments for insertion
      const equipmentAssignments = selectedEquipment.map(item => ({
        guard_id: values.guardId,
        equipment_type_id: item.equipmentId,
        quantity: item.quantity,
        notes: values.notes || null,
        assigned_by: user.id,
      }));
      
      // Insert assignments
      const { error: assignmentError } = await supabase
        .from('guard_equipment')
        .insert(equipmentAssignments);
        
      if (assignmentError) throw assignmentError;
      
      toast({
        title: "Equipment assigned",
        description: "Equipment has been successfully assigned to the guard",
      });
      
      // Reset form
      form.reset({
        guardId: "",
        notes: "",
        equipmentAssignments: equipment.map(item => ({
          equipmentId: item.id,
          quantity: 1,
          isSelected: false,
        })),
      });
      
      // Refresh data
      onDataChange();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to assign equipment: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Assign Equipment to Guards</h2>
      
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Guard Selection */}
            <FormField
              control={form.control}
              name="guardId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Select Guard</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a guard" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {guards.map((guard) => (
                        <SelectItem key={guard.id} value={guard.id}>
                          {guard.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Equipment Selection */}
            <div>
              <FormLabel className="block mb-2">Select Equipment</FormLabel>
              <Card className="border border-gray-200">
                <CardContent className="p-4 max-h-96 overflow-y-auto">
                  <div className="space-y-4">
                    {equipment.length === 0 ? (
                      <p className="text-muted-foreground">No equipment available.</p>
                    ) : (
                      equipment.map((item, index) => (
                        <div key={item.id} className="flex items-center space-x-4 p-2 border-b last:border-b-0">
                          <FormField
                            control={form.control}
                            name={`equipmentAssignments.${index}.isSelected`}
                            render={({ field }) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          
                          <div className="flex-grow">
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-muted-foreground">
                              Cost: €{item.base_price.toFixed(2)}
                            </p>
                          </div>
                          
                          {form.watch(`equipmentAssignments.${index}.isSelected`) && (
                            <FormField
                              control={form.control}
                              name={`equipmentAssignments.${index}.quantity`}
                              render={({ field }) => (
                                <FormItem className="w-20">
                                  <FormControl>
                                    <Input
                                      type="number"
                                      min="1"
                                      max={100}
                                      {...field}
                                      onChange={(e) => 
                                        field.onChange(parseInt(e.target.value, 10))
                                      }
                                      className="w-20"
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
              {form.formState.errors.equipmentAssignments?.message && (
                <p className="text-sm text-red-500 mt-2">
                  {form.formState.errors.equipmentAssignments.message}
                </p>
              )}
            </div>
            
            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Add any notes about this assignment" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button type="submit" className="w-full">
              Assign Equipment
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
};
